import React, { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/services/apiFetch'
import { Button } from '@/components/ui/Button'
import type { ProductionOrder } from '@/services/productionOrders'
import { X, AlertTriangle, CheckCircle, Package, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react'

export interface ProductionDemandModalProps {
  order: ProductionOrder | null
  isOpen: boolean
  onClose: () => void
  onSuccess?: (msg: string) => void
}

export interface PreviewItem {
  material_id: string | null
  material_code: string | null
  material_name: string
  uom: string
  qty_gross: number
  resolution_status: 'resolved' | 'unresolved'
  physical_available: number
  reserved_base: number
  available_to_reserve: number
  estimated_reserve: number
  estimated_shortage: number
}

export interface AmbiguousConfigSelectorProps {
  ambiguousReason: string
  hasAmbiguousWood: boolean
  woodCandidates: Array<{ id: string; label: string }>
  selectedWoodConfigId: string
  onSelectWoodConfigId: (id: string) => void
  hasAmbiguousFoam: boolean
  foamCandidates: Array<{ id: string; label: string }>
  selectedFoamConfigId: string
  onSelectFoamConfigId: (id: string) => void
  loading?: boolean
  actionLoading?: boolean
  onApply: () => void
}

export function AmbiguousConfigSelector({
  ambiguousReason,
  hasAmbiguousWood,
  woodCandidates,
  selectedWoodConfigId,
  onSelectWoodConfigId,
  hasAmbiguousFoam,
  foamCandidates,
  selectedFoamConfigId,
  onSelectFoamConfigId,
  loading = false,
  actionLoading = false,
  onApply,
}: AmbiguousConfigSelectorProps): React.ReactElement {
  const isApplyDisabled =
    loading ||
    actionLoading ||
    (hasAmbiguousWood && !selectedWoodConfigId) ||
    (hasAmbiguousFoam && !selectedFoamConfigId)

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        Cấu hình BOM chưa xác định (Trùng lặp cấu hình)
      </div>
      <p className="text-xs text-amber-800">{ambiguousReason}</p>

      {hasAmbiguousWood && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">
            Chọn cấu hình BOM sản phẩm / phần cứng (Hardware):
          </label>
          <select
            value={selectedWoodConfigId}
            onChange={(e) => onSelectWoodConfigId(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
          >
            <option value="">-- Chọn cấu hình --</option>
            {woodCandidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {hasAmbiguousFoam && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">
            Chọn cấu hình đóng gói / bao bì mút xốp:
          </label>
          <select
            value={selectedFoamConfigId}
            onChange={(e) => onSelectFoamConfigId(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
          >
            <option value="">-- Chọn cấu hình --</option>
            {foamCandidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={isApplyDisabled}
          onClick={onApply}
        >
          Áp dụng cấu hình & Xem lại
        </Button>
      </div>
    </div>
  )
}

export function DemandPreviewTable({ lines }: { lines: PreviewItem[] }): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span>Xem trước lượng giữ từ tồn khả dụng thực tế:</span>
        <span className="font-semibold text-slate-800">{lines.length} danh mục vật tư</span>
      </div>

      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
          <thead className="bg-slate-50 font-medium text-slate-700">
            <tr>
              <th className="px-3 py-2.5 w-12 text-center">STT</th>
              <th className="px-3 py-2.5">Tên vật tư</th>
              <th className="px-2 py-2.5 text-center">ĐVT</th>
              <th className="px-3 py-2.5 text-right">Nhu cầu</th>
              <th className="px-3 py-2.5 text-right text-slate-500">Tồn vật lý</th>
              <th className="px-3 py-2.5 text-right font-semibold text-emerald-800">Khả dụng</th>
              <th className="px-3 py-2.5 text-right font-semibold text-blue-800">Dự kiến giữ</th>
              <th className="px-3 py-2.5 text-right font-semibold text-rose-700">Thiếu</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {lines.map((p, idx) => (
              <tr key={p.material_id || idx} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-center text-slate-400 tabular-nums">{idx + 1}</td>
                <td className="px-3 py-2 text-slate-700">{p.material_name}</td>
                <td className="px-2 py-2 text-center text-slate-500">{p.uom}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{p.qty_gross}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{p.physical_available}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-800">{p.available_to_reserve}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-800">{p.estimated_reserve}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700">
                  {p.estimated_shortage > 0 ? p.estimated_shortage : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface ReservationItem {
  requirement_line_id: string
  material_id: string | null
  material_code: string | null
  material_name: string
  uom: string
  required: number
  reserved_total: number
  consumed: number
  remaining_reserved: number
  shortage: number
  physical_available: number
  available_to_reserve_after: number
  reservations: Array<{
    id: string
    qty_reserved_base: string
    qty_consumed_base: string
    status: 'ACTIVE' | 'CONSUMED' | 'RELEASED'
  }>
}

interface ActiveBatchResult {
  batch_id: string
  plan_line_id: string | null
  status: string
  lines: ReservationItem[]
}

export function ProductionDemandModal({
  order,
  isOpen,
  onClose,
  onSuccess,
}: ProductionDemandModalProps): React.ReactElement | null {
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [activeBatch, setActiveBatch] = useState<ActiveBatchResult | null>(null)

  // Preview state
  const [previewData, setPreviewData] = useState<PreviewItem[] | null>(null)
  const [isAmbiguous, setIsAmbiguous] = useState(false)
  const [ambiguousReason, setAmbiguousReason] = useState<string>('')
  const [hasAmbiguousFoam, setHasAmbiguousFoam] = useState(false)
  const [foamCandidates, setFoamCandidates] = useState<Array<{ id: string; label: string }>>([])
  const [hasAmbiguousWood, setHasAmbiguousWood] = useState(false)
  const [woodCandidates, setWoodCandidates] = useState<Array<{ id: string; label: string }>>([])
  const [selectedFoamConfigId, setSelectedFoamConfigId] = useState<string>('')
  const [selectedWoodConfigId, setSelectedWoodConfigId] = useState<string>('')

  // Issue dialog state
  const [isIssueOpen, setIsIssueOpen] = useState(false)
  const [issueQtys, setIssueQtys] = useState<Record<string, number>>({})
  const [issueIdempotencyKey, setIssueIdempotencyKey] = useState<string | null>(null)

  // Load active requirement batch or fallback to preview
  const loadRequirementStatus = useCallback(
    async (overrides?: { woodConfigId?: string; foamConfigId?: string }) => {
      if (!order) return
      setLoading(true)
      setErrorMsg(null)
      try {
        // 1. Check if active batch already exists
        const res = await apiFetch(`/api/production-orders/${order.id}/requirements`)
        if (res.ok) {
          const json = await res.json()
          if (json.data) {
            setActiveBatch(json.data)
            setPreviewData(null)
            setIsAmbiguous(false)
            setLoading(false)
            return
          }
        }

        // 2. If no batch exists, load preview
        setActiveBatch(null)
        const woodId = overrides?.woodConfigId !== undefined ? overrides.woodConfigId : selectedWoodConfigId
        const foamId = overrides?.foamConfigId !== undefined ? overrides.foamConfigId : selectedFoamConfigId

        const reqBody: Record<string, string> = {}
        if (woodId) reqBody.override_wood_config_id = woodId
        if (foamId) reqBody.override_foam_config_id = foamId

        const previewRes = await apiFetch(`/api/production-orders/${order.id}/requirements/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reqBody),
        })
        const previewJson = await previewRes.json()

        if (previewJson.preview?.status === 'config_ambiguous') {
          setIsAmbiguous(true)
          setAmbiguousReason(previewJson.preview.reason || 'Cấu hình BOM bị trùng lặp. Vui lòng chọn cấu hình cụ thể.')
          const amb = previewJson.preview.ambiguous || {}
          if (amb.foam?.candidates && amb.foam.candidates.length > 0) {
            setHasAmbiguousFoam(true)
            setFoamCandidates(
              amb.foam.candidates.map((c: any) => ({
                id: c.id,
                label: `${c.packaging_type === 'thuong' ? 'Đóng gói thường' : c.packaging_type === 'ecom' ? 'Đóng gói Ecom' : (c.packaging_type || 'Bao bì')} (${c.id})`,
              })),
            )
          } else {
            setHasAmbiguousFoam(false)
            setFoamCandidates([])
          }

          if (amb.wood?.candidates && amb.wood.candidates.length > 0) {
            setHasAmbiguousWood(true)
            setWoodCandidates(
              amb.wood.candidates.map((c: any) => {
                const details = [
                  c.code,
                  c.colors?.length ? `Màu: ${c.colors.join(', ')}` : null,
                  c.woods?.length ? `Gỗ: ${c.woods.join(', ')}` : null,
                ].filter(Boolean).join(' - ')
                return {
                  id: c.id,
                  label: details ? `${details} (${c.id})` : `Cấu hình ${c.id}`,
                }
              }),
            )
          } else {
            setHasAmbiguousWood(false)
            setWoodCandidates([])
          }

          setPreviewData(null)
        } else if (previewJson.preview?.status === 'ok') {
          setIsAmbiguous(false)
          setHasAmbiguousFoam(false)
          setHasAmbiguousWood(false)
          setPreviewData(previewJson.preview.lines || [])
        } else {
          setErrorMsg(previewJson.preview?.reason || previewJson.error?.message || 'Không thể tạo bản xem trước BOM')
        }
      } catch (err: any) {
        setErrorMsg(err.message || 'Lỗi kết nối máy chủ')
      } finally {
        setLoading(false)
      }
    },
    [order, selectedWoodConfigId, selectedFoamConfigId],
  )

  useEffect(() => {
    if (isOpen && order) {
      setSelectedFoamConfigId('')
      setSelectedWoodConfigId('')
      setHasAmbiguousFoam(false)
      setHasAmbiguousWood(false)
      setFoamCandidates([])
      setWoodCandidates([])
      void loadRequirementStatus({ woodConfigId: '', foamConfigId: '' })
    } else {
      setActiveBatch(null)
      setPreviewData(null)
      setIsAmbiguous(false)
      setErrorMsg(null)
      setIsIssueOpen(false)
      setSelectedFoamConfigId('')
      setSelectedWoodConfigId('')
    }
  }, [isOpen, order?.id])

  // Action: Create and reserve
  const handleCreateAndReserve = async () => {
    if (!order) return
    setActionLoading(true)
    setErrorMsg(null)
    try {
      const body: Record<string, string> = {}
      if (selectedWoodConfigId) body.override_wood_config_id = selectedWoodConfigId
      if (selectedFoamConfigId) body.override_foam_config_id = selectedFoamConfigId

      const res = await apiFetch(`/api/production-orders/${order.id}/requirements/create-and-reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!res.ok) {
        throw new Error(json.error?.message || 'Không thể tạo nhu cầu và giữ vật tư')
      }
      onSuccess?.('Đã tạo nhu cầu và giữ vật tư thành công!')
      await loadRequirementStatus({ woodConfigId: selectedWoodConfigId, foamConfigId: selectedFoamConfigId })
    } catch (err: any) {
      setErrorMsg(err.message || 'Có lỗi xảy ra khi tạo nhu cầu')
    } finally {
      setActionLoading(false)
    }
  }

  // Action: Release all active reservations
  const handleReleaseReservations = async () => {
    if (!order) return
    if (!window.confirm('Bạn có chắc chắn muốn hủy giữ hàng cho lệnh sản xuất này? Lượng vật tư còn lại sẽ được trả về kho khả dụng.')) {
      return
    }
    setActionLoading(true)
    setErrorMsg(null)
    try {
      const res = await apiFetch(`/api/production-orders/${order.id}/reservations/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Người dùng hủy giữ vật tư' }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error?.message || 'Không thể hủy giữ vật tư')
      onSuccess?.(`Đã hủy giữ ${json.released_count ?? 0} dòng vật tư`)
      await loadRequirementStatus()
    } catch (err: any) {
      setErrorMsg(err.message || 'Có lỗi xảy ra khi hủy giữ vật tư')
    } finally {
      setActionLoading(false)
    }
  }

  // Action: Cancel production batch
  const handleCancelBatch = async () => {
    if (!order || !activeBatch) return
    if (!window.confirm('Bạn có chắc muốn hủy đợt nhu cầu vật tư này? Toàn bộ vật tư đang giữ sẽ được giải phóng.')) {
      return
    }
    setActionLoading(true)
    setErrorMsg(null)
    try {
      const res = await apiFetch(`/api/production-orders/${order.id}/requirements/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: activeBatch.batch_id, reason: 'Hủy đợt nhu cầu sản xuất' }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error?.message || 'Không thể hủy đợt nhu cầu')
      onSuccess?.('Đã hủy đợt nhu cầu thành công!')
      await loadRequirementStatus()
    } catch (err: any) {
      setErrorMsg(err.message || 'Có lỗi xảy ra')
    } finally {
      setActionLoading(false)
    }
  }

  // Action: Top-up partial reservation from newly available stock
  const handleReserveAvailable = async () => {
    if (!order) return
    setActionLoading(true)
    setErrorMsg(null)
    try {
      const res = await apiFetch(`/api/production-orders/${order.id}/reservations/reserve-available`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const json = await res.json()
      if (!res.ok) {
        throw new Error(json.error?.message || 'Không thể giữ thêm vật tư')
      }
      onSuccess?.('Đã kiểm tra và giữ thêm vật tư thành công!')
      await loadRequirementStatus()
    } catch (err: any) {
      setErrorMsg(err.message || 'Có lỗi xảy ra khi giữ thêm vật tư')
    } finally {
      setActionLoading(false)
    }
  }

  // Open Issue sub-dialog
  const handleOpenIssueDialog = () => {
    if (!activeBatch) return
    const initial: Record<string, number> = {}
    for (const line of activeBatch.lines) {
      for (const r of line.reservations) {
        if (r.status === 'ACTIVE') {
          const remaining = Number(r.qty_reserved_base) - Number(r.qty_consumed_base)
          if (remaining > 0) {
            initial[r.id] = remaining
          }
        }
      }
    }
    setIssueQtys(initial)
    setIssueIdempotencyKey(null)
    setIsIssueOpen(true)
  }

  // Submit Issue
  const handleSubmitIssue = async () => {
    if (!order || !activeBatch) return
    const linesToIssue: Array<{ reservation_id: string; qty_base: number }> = []

    for (const [resId, qty] of Object.entries(issueQtys)) {
      if (qty > 0) {
        linesToIssue.push({ reservation_id: resId, qty_base: qty })
      }
    }

    if (linesToIssue.length === 0) {
      alert('Vui lòng nhập số lượng xuất > 0 cho ít nhất một vật tư')
      return
    }

    setActionLoading(true)
    setErrorMsg(null)

    // Reuse existing key on retry, or generate a fresh key for this submission payload
    const idempotencyKey =
      issueIdempotencyKey || `iss-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    if (!issueIdempotencyKey) {
      setIssueIdempotencyKey(idempotencyKey)
    }

    try {
      const res = await apiFetch(`/api/production-orders/${order.id}/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          purpose: `Xuất vật tư sản xuất cho lệnh ${order.variant_code || order.id}`,
          lines: linesToIssue,
          idempotency_key: idempotencyKey,
        }),
      })
      const json = await res.json()
      if (!res.ok) {
        throw new Error(json.error?.message || 'Lỗi xuất kho sản xuất')
      }
      setIssueIdempotencyKey(null)
      setIsIssueOpen(false)
      onSuccess?.('Đã xuất kho sản xuất thành công!')
      await loadRequirementStatus()
    } catch (err: any) {
      setErrorMsg(err.message || 'Có lỗi xảy ra khi xuất kho')
    } finally {
      setActionLoading(false)
    }
  }

  if (!isOpen || !order) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              <h2 className="text-base font-semibold text-slate-900">
                Nhu cầu & Giữ vật tư sản xuất
              </h2>
            </div>
            <p className="mt-1 text-xs text-slate-500 font-mono">
              Biến thể: <span className="font-semibold text-slate-700">{order.variant_code}</span> | 
              SL kế hoạch: <span className="font-semibold text-slate-700">{order.planned_qty}</span> SP
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Scope Banner */}
          <div className="rounded-md border border-blue-100 bg-blue-50/50 p-3 text-xs text-blue-800 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <strong>Phạm vi tự động V1:</strong> Hệ thống tự động tính BOM và giữ tồn kho khả dụng cho{' '}
              <strong>Ngũ kim, Mút xốp và Bao bì</strong>. Vật tư Gỗ được xuất thủ công theo kế hoạch xưởng.
            </div>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
              <span>{errorMsg}</span>
            </div>
          )}

          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-500">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin text-blue-600" />
              Đang tải dữ liệu BOM & Tồn kho khả dụng...
            </div>
          ) : isAmbiguous ? (
            /* Ambiguous Config Selection */
            <AmbiguousConfigSelector
              ambiguousReason={ambiguousReason}
              hasAmbiguousWood={hasAmbiguousWood}
              woodCandidates={woodCandidates}
              selectedWoodConfigId={selectedWoodConfigId}
              onSelectWoodConfigId={setSelectedWoodConfigId}
              hasAmbiguousFoam={hasAmbiguousFoam}
              foamCandidates={foamCandidates}
              selectedFoamConfigId={selectedFoamConfigId}
              onSelectFoamConfigId={setSelectedFoamConfigId}
              loading={loading}
              actionLoading={actionLoading}
              onApply={() =>
                void loadRequirementStatus({
                  woodConfigId: selectedWoodConfigId,
                  foamConfigId: selectedFoamConfigId,
                })
              }
            />
          ) : activeBatch ? (
            /* Active Frozen Requirement Batch & Reservation Status */
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50/50 px-4 py-2.5">
                <div className="flex items-center gap-2 text-xs text-emerald-900">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span>
                    Đợt nhu cầu: <strong className="font-mono">{activeBatch.batch_id}</strong> (Đã chốt)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {activeBatch.lines.some((l) => l.shortage > 0) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={actionLoading}
                      onClick={() => void handleReserveAvailable()}
                    >
                      Giữ thêm vật tư
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={actionLoading || activeBatch.lines.every((l) => l.remaining_reserved <= 0)}
                    onClick={handleOpenIssueDialog}
                  >
                    Xuất vật tư SX
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={actionLoading || activeBatch.lines.every((l) => l.remaining_reserved <= 0)}
                    onClick={() => void handleReleaseReservations()}
                  >
                    Hủy giữ hàng
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                    disabled={actionLoading}
                    onClick={() => void handleCancelBatch()}
                  >
                    Hủy đợt
                  </Button>
                </div>
              </div>

              {/* Reservations Table */}
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                  <thead className="bg-slate-50 font-medium text-slate-700">
                    <tr>
                      <th className="px-3 py-2.5 w-12 text-center">STT</th>
                      <th className="px-3 py-2.5">Tên vật tư</th>
                      <th className="px-2 py-2.5 text-center">ĐVT</th>
                      <th className="px-3 py-2.5 text-right">Nhu cầu</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-blue-800">Đã giữ</th>
                      <th className="px-3 py-2.5 text-right text-emerald-800">Đã xuất</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-amber-800">Còn giữ</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-rose-700">Thiếu</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {activeBatch.lines.map((line, idx) => (
                      <tr key={line.requirement_line_id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-center text-slate-400 tabular-nums">{idx + 1}</td>
                        <td className="px-3 py-2 text-slate-700">{line.material_name}</td>
                        <td className="px-2 py-2 text-center text-slate-500">{line.uom}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{line.required}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-800">{line.reserved_total}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{line.consumed}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-800">{line.remaining_reserved}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700">
                          {line.shortage > 0 ? line.shortage : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : previewData ? (
            /* Preview Table & Action to Create */
            <DemandPreviewTable lines={previewData} />
          ) : null}

          {/* Sub-dialog: Xuất vật tư SX */}
          {isIssueOpen && activeBatch && (
            <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4">
              <div className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-2xl space-y-4">
                <div className="flex items-center justify-between border-b pb-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Phiếu xuất kho sản xuất theo đơn lệnh
                  </h3>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-600"
                    onClick={() => setIsIssueOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <p className="text-xs text-slate-500">
                  Nhập số lượng xuất cho từng vật tư đang giữ. Không thể xuất vượt quá số lượng giữ còn lại.
                </p>

                <div className="max-h-72 overflow-y-auto space-y-2 border rounded p-2">
                  {activeBatch.lines.map((line) => {
                    const activeRes = line.reservations.find((r) => r.status === 'ACTIVE')
                    if (!activeRes) return null
                    const remaining = Number(activeRes.qty_reserved_base) - Number(activeRes.qty_consumed_base)
                    if (remaining <= 0) return null

                    return (
                      <div
                        key={line.requirement_line_id}
                        className="flex items-center justify-between border-b border-slate-100 py-2 text-xs last:border-none"
                      >
                        <div className="flex-1 pr-2">
                          <div className="font-medium text-slate-800">{line.material_name}</div>
                          <div className="text-slate-500 text-[11px]">
                            Còn giữ: <span className="font-semibold text-blue-700">{remaining}</span> {line.uom}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max={remaining}
                            step="any"
                            value={issueQtys[activeRes.id] ?? 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0
                              setIssueQtys((prev) => ({
                                ...prev,
                                [activeRes.id]: Math.min(remaining, Math.max(0, val)),
                              }))
                              setIssueIdempotencyKey(null)
                            }}
                            className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-xs font-mono focus:border-blue-500 focus:outline-none"
                          />
                          <span className="text-slate-500 w-8">{line.uom}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button size="sm" variant="ghost" onClick={() => setIsIssueOpen(false)}>
                    Hủy
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={actionLoading}
                    onClick={() => void handleSubmitIssue()}
                  >
                    Xác nhận xuất kho
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-3">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Đóng
          </Button>
          {!activeBatch && previewData && !isAmbiguous && (
            <Button
              size="sm"
              variant="primary"
              disabled={actionLoading || previewData.length === 0}
              onClick={() => void handleCreateAndReserve()}
            >
              <ArrowRight className="mr-1.5 h-4 w-4" />
              Tạo nhu cầu & Giữ vật tư
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
